import React, { useCallback, useMemo, useState } from "react";

import { orderBy } from "lodash";

import { TemplateFragment } from "../../../graphql/fragments";
import {
  ReportType,
  TemplateQuestion as TemplateQuestionType,
  TemplateSection as TemplateSectionType,
} from "../../../graphql/types";

import useTranslations from "../../../hooks/useTranslations";

import { CheckIcon, EyeOpenIcon } from "../../icons";

import Avatar from "../../wombat-ui/Avatar";
import Select from "../../wombat-ui/components/Form/Select";
import {
  Button,
  Modal,
  ModalActions,
  ModalBody,
  ModalHeader,
} from "../../wombat-ui";
import TemplateQuestion from "../../reports/TemplateQuestion";
import TemplateSection from "../../reports/TemplateSection";

type TemplatePreviewModalProps = {
  template: TemplateFragment;
  toggled: boolean;
  onClose: () => void;
  onSelect: () => void;
};

const TemplatePreviewModal: React.FC<TemplatePreviewModalProps> = ({
  template,
  toggled,
  onClose,
  onSelect,
}) => {
  const t = useTranslations();
  const [selectedReportType, setSelectedReportType] = useState<
    ReportType | "All"
  >("All");

  const reportTypeItem = useMemo(
    (): { value: ReportType | "All"; label: string }[] => [
      { value: "All", label: t("mads.allQuestions") as string },
      { value: "FirstReport", label: t("mads.firstReportQuestions") as string },
      {
        value: "IntermediateReport",
        label: t("mads.intermediateReportQuestions") as string,
      },
      { value: "FinalReport", label: t("mads.finalReportQuestions") as string },
    ],
    []
  );

  const sectionPerReportType = useMemo(() => {
    const sections: {
      [key in ReportType | "All"]: TemplateSectionType[];
    } = {
      All: [],
      FirstReport: [],
      IntermediateReport: [],
      FinalReport: [],
    };

    const reportTypes: ReportType[] = [
      "FirstReport",
      "IntermediateReport",
      "FinalReport",
    ];

    orderBy(template.templateSections, "order").forEach((s) => {
      sections.All.push(s);

      reportTypes.map((rt) => {
        if (s.templateQuestions.some((a) => a.reportTypes.includes(rt))) {
          sections[rt].push({
            ...s,
            templateQuestions: s.templateQuestions.filter((a) =>
              a.reportTypes.includes(rt)
            ),
          });
        }
      });
    });

    return sections;
  }, [template]);

  return (
    <Modal
      isOpen={toggled}
      aria-label="Template preview"
      id={`${template.id}-preview`}
    >
      <ModalHeader
        onClose={onClose}
        title={
          t("mads.templatePreviewTitle", { title: template.title }) as string
        }
      >
        <p>{t("mads.templatePreviewTitle", { title: template.title })}</p>
        <p className="text-sm font-normal text-dark-100">
          {t("mads.templatePreviewSubtitle")}
        </p>
      </ModalHeader>
      <ModalBody>
        <div className="flex pb-4">
          <div className="flex flex-col flex-grow space-y-2 sm:max-w-md">
            <h4 className="text-sm font-semibold text-primary-900">
              {t("mads.view")}
            </h4>
            <Select
              id="question-visibility-select"
              label={t("mads.viewText") as string}
              items={reportTypeItem}
              defaultValue={reportTypeItem[0]}
              renderItem={(i) => i.label}
              searchable
              onChange={(i) => {
                setSelectedReportType(i.value);
              }}
              searchOption={{
                keys: ["label"],
                threshold: 0.2,
                useExtendedSearch: true,
              }}
              getValue={(i) => i.value}
              srOnlyLabel
            />
          </div>
        </div>

        <div className="px-6 py-4 -mx-4 space-y-6 bg-light-0">
          {sectionPerReportType[selectedReportType].map((section, index) => (
            <TemplateSection section={section} key={section.id}>
              {section.templateQuestions.map((question, index) => (
                <TemplateQuestion
                  preview
                  index={index}
                  question={question}
                  key={question.id}
                />
              ))}
            </TemplateSection>
          ))}
        </div>
      </ModalBody>
      <ModalActions>
        <Button
          variant="subtle"
          onClick={onClose}
          id={`closeTemplatePreviewModal-${template.id}`}
        >
          {t("forms.actions.close")}
        </Button>
        <Button
          variant="primary"
          onClick={onSelect}
          id={`useTemplate-${template.id}`}
        >
          {t("forms.actions.use")}
        </Button>
      </ModalActions>
    </Modal>
  );
};
type TemplatePreviewCardProps = {
  template: TemplateFragment;
  onSelect: (id: string) => void;
};
export const TemplatePreviewCard: React.FC<TemplatePreviewCardProps> = React.memo(
  ({ template, onSelect }) => {
    const t = useTranslations();
    const [previewToggled, setPreviewToggled] = useState(false);

    const _onSelect = useCallback(() => onSelect(template.slug), [
      template,
      onSelect,
    ]);

    return (
      <li
        className="col-span-1 bg-white divide-y divide-gray-200 rounded-lg shadow"
        id={`template-card-${template.id}`}
      >
        <TemplatePreviewModal
          template={template}
          toggled={previewToggled}
          onClose={() => setPreviewToggled(false)}
          onSelect={_onSelect}
        />
        <div className="flex items-center justify-between w-full p-6 space-x-6">
          <div className="flex-1 truncate">
            <div className="flex items-center space-x-3">
              <h3 className="text-sm font-medium text-gray-900 truncate">
                {template.title}
              </h3>
            </div>
            <p className="mt-1 text-sm text-gray-500 truncate">
              {t("templates.questionsNumber", {
                number: template.templateSections
                  .map((e) => e.templateQuestions)
                  .flat().length,
              })}
            </p>
          </div>
          <Avatar size={10} type="document" alt="Template" />
        </div>
        <div>
          <div className="flex -mt-px divide-x divide-gray-200">
            <div className="flex flex-1 w-0">
              <div
                role="button"
                id={`template-preview-modal-trigger-${template.id}`}
                tabIndex={0}
                className="relative inline-flex items-center justify-center flex-1 w-0 py-3 -mr-px text-sm font-medium text-gray-700 border border-transparent rounded-bl-lg hover:text-gray-500"
                onClick={() => setPreviewToggled(true)}
              >
                <EyeOpenIcon iconClassName="w-6 h-6 text-primary-500" />
                <span className="ml-3">{t("forms.actions.view")}</span>
              </div>
            </div>
            <div className="flex flex-1 w-0 -ml-px">
              <div
                role="button"
                id={`template-select-${template.id}`}
                tabIndex={0}
                className="relative inline-flex items-center justify-center flex-1 w-0 py-3 text-sm font-medium text-gray-700 border border-transparent rounded-br-lg hover:text-gray-500"
                onClick={_onSelect}
              >
                <CheckIcon iconClassName="w-6 h-6 text-primary-500" />

                <span className="ml-3">{t("forms.actions.use")}</span>
              </div>
            </div>
          </div>
        </div>
      </li>
    );
  }
);
